/**
 * Shared metric heatmap.
 *
 * Ported from `components/pipeline/normalize/normalized-matrix.tsx` and reused
 * by the Summarized tab, exactly as the reference app does. Metrics are grouped
 * by category with rotated headers; cells are banded ≥80 / 50–79 / <50 / missing.
 */
import { useMemo } from "react";

import type { Matrix } from "../data/types";

export type MatrixValueMode = "normalized" | "actual";

function bandClass(v: number | null): string {
  if (v == null) return "bg-gray-100 text-gray-400";
  if (v >= 80) return "bg-green-100 text-green-900";
  if (v >= 50) return "bg-amber-100 text-amber-900";
  return "bg-red-100 text-red-900";
}

function formatActual(v: number | null, uom: string | null): string {
  if (v == null) return "—";
  if (uom === "USD") return v >= 1000 ? `${Math.round(v / 1000)}k` : String(v);
  return Math.abs(v) >= 1000 ? v.toLocaleString() : String(v);
}

export function MetricMatrix({
  matrix,
  mode,
  rowHeaderLabel,
}: {
  matrix: Matrix;
  mode: MatrixValueMode;
  rowHeaderLabel: string;
}) {
  /** Category → metrics, preserving catalog order so columns stay stable. */
  const groups = useMemo(() => {
    const out: { category: string; metricIds: string[] }[] = [];
    matrix.metrics.forEach(m => {
      const last = out[out.length - 1];
      if (last && last.category === m.category)
        last.metricIds.push(m.metric_id);
      else out.push({ category: m.category, metricIds: [m.metric_id] });
    });
    return out;
  }, [matrix.metrics]);

  const uomById = useMemo(
    () => Object.fromEntries(matrix.metrics.map(m => [m.metric_id, m.uom])),
    [matrix.metrics]
  );
  const nameById = useMemo(
    () =>
      Object.fromEntries(matrix.metrics.map(m => [m.metric_id, m.metric_name])),
    [matrix.metrics]
  );

  return (
    <div>
      {/* Grid scrolls inside the tab; legend stays fixed below. */}
      <div className="max-h-[min(55vh,560px)] overflow-auto overscroll-contain">
        <table className="border-collapse text-[11px]">
          <thead>
            {/* Category band */}
            <tr>
              <th className="sticky left-0 z-20 border-r border-gray-200 bg-white" />
              {groups.map(g => (
                <th
                  key={g.category}
                  colSpan={g.metricIds.length}
                  className="border-l border-gray-200 px-2 pb-1 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500"
                >
                  {g.category}
                </th>
              ))}
            </tr>
            {/* Rotated metric names */}
            <tr>
              <th className="sticky left-0 z-20 whitespace-nowrap border-r border-gray-200 bg-white pr-3 text-left align-bottom text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {rowHeaderLabel} \ Metric
              </th>
              {groups.flatMap(g =>
                g.metricIds.map((id, i) => (
                  <th
                    key={id}
                    // Fixed width + overflow-hidden keeps a long rotated label
                    // inside its own column instead of bleeding across the grid.
                    className={`h-40 w-7 overflow-hidden p-0 align-bottom ${
                      i === 0 ? "border-l border-gray-200" : ""
                    }`}
                    title={nameById[id]}
                  >
                    <div className="flex h-40 items-end justify-center">
                      <span className="max-h-40 overflow-hidden whitespace-nowrap rotate-180 font-normal text-gray-600 [writing-mode:vertical-rl]">
                        {nameById[id]}
                      </span>
                    </div>
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.length === 0 ? (
              <tr>
                <td
                  colSpan={1 + matrix.metrics.length}
                  className="py-10 text-center text-sm text-gray-500"
                >
                  No rows for this view.
                </td>
              </tr>
            ) : null}
            {matrix.rows.map(row => (
              <tr key={row.key}>
                <td className="sticky left-0 z-20 whitespace-nowrap border-r border-gray-200 bg-white pr-3 font-mono text-[11px] text-gray-700">
                  {row.label}
                </td>
                {groups.flatMap(g =>
                  g.metricIds.map((id, i) => {
                    const cell = row.cells[id];
                    const v =
                      mode === "normalized"
                        ? (cell?.normalized ?? null)
                        : (cell?.actual ?? null);
                    return (
                      <td
                        key={id}
                        className={`w-7 min-w-7 px-1 py-1 text-left tabular-nums ${i === 0 ? "border-l border-gray-200" : ""} ${
                          mode === "normalized"
                            ? bandClass(cell?.normalized ?? null)
                            : "text-gray-700"
                        }`}
                        title={`${nameById[id]} · ${row.label}`}
                      >
                        {mode === "normalized"
                          ? v == null
                            ? "—"
                            : v.toFixed(1)
                          : formatActual(v, uomById[id] ?? null)}
                      </td>
                    );
                  })
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px] text-gray-500">
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-200" />{" "}
          ≥ 80
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-200" />{" "}
          50–79
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-200" />{" "}
          &lt; 50
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-200" />{" "}
          missing
        </span>
      </div>
    </div>
  );
}

export default MetricMatrix;
